import React, { useCallback, useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';

const useStyles = makeStyles(theme => ({
  tableHead: {
    backgroundColor: theme.palette.background.default,
  },
  headCell: {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  idCell: {
    color: theme.palette.text.disabled,
    fontSize: '0.8rem',
    width: 40,
  },
  nameCell: {
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  dataCell: {
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
  },
  addRow: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  addButton: {
    margin: theme.spacing(1, 1.5),
  },
  formControl: {
    minWidth: 340,
    marginTop: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: '0.85rem',
  },
  emptyBox: {
    padding: theme.spacing(2),
  },
}));

interface ProviderRow {
  ref: string;
  name: string;
  prefix: string;
  envType: string;
  azureSubscription: string;
  resourceGroup: string;
  azureRegion: string;
}

export function AzureEnvironmentProvidersCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const meta = entity.metadata as Record<string, any>;
  const organization = meta.adoOrganization as string | undefined;
  const project = meta.adoProject as string | undefined;
  const repoName = meta.adoRepoName as string | undefined;
  const envEntityRef = `azureenvironment:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;

  const dependsOn: string[] = ((entity.spec as any)?.dependsOn ?? []).filter(
    (r: string) => r.toLowerCase().startsWith('azureenvironmentprovider:'),
  );

  const canModify = !!(organization && project && repoName);

  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [availableProviders, setAvailableProviders] = useState<{ ref: string; label: string }[]>([]);
  const [selectedAddRef, setSelectedAddRef] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  // Detach dialog
  const [detachRow, setDetachRow] = useState<ProviderRow | null>(null);
  const [detachLoading, setDetachLoading] = useState(false);
  const [detachError, setDetachError] = useState('');

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await Promise.all(
        dependsOn.map(async ref => {
          const e = await catalogApi.getEntityByRef(ref).catch(() => null);
          if (!e) {
            const name = ref.split('/').pop() ?? ref;
            return { ref, name, prefix: '—', envType: '—', azureSubscription: '—', resourceGroup: '—', azureRegion: '—' };
          }
          const pm = e.metadata as Record<string, any>;
          return {
            ref,
            name: e.metadata.name,
            prefix: (pm.prefix as string) || '—',
            envType: (pm.envType as string) || '—',
            azureSubscription: (pm.azureSubscription as string) || '—',
            resourceGroup: (pm.resourceGroup as string) || '—',
            azureRegion: (pm.azureRegion as string) || '—',
          };
        }),
      );
      setProviders(rows);
    } finally {
      setLoading(false);
    }
  }, [dependsOn.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const openAddDialog = async () => {
    setAddError('');
    setSelectedAddRef('');
    setAddOpen(true);
    try {
      const result = await catalogApi.getEntities({ filter: { kind: 'AzureEnvironmentProvider' } });
      const currentRefs = new Set(dependsOn.map(r => r.toLowerCase()));
      const opts = result.items
        .filter(e => {
          const ref = `azureenvironmentprovider:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`;
          return !currentRefs.has(ref.toLowerCase());
        })
        .map(e => {
          const pm = e.metadata as Record<string, any>;
          const prefix = (pm.prefix as string) ?? '';
          const region = (pm.azureRegion as string) ?? '';
          const label = [e.metadata.name, prefix && `(${prefix})`, region && `— ${region}`]
            .filter(Boolean).join(' ');
          return {
            ref: `azureenvironmentprovider:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`,
            label,
          };
        });
      setAvailableProviders(opts);
    } catch {
      setAddError('Failed to load available providers.');
    }
  };

  const handleAdd = async () => {
    if (!selectedAddRef || !organization || !project || !repoName) return;
    setAddLoading(true);
    setAddError('');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/environments/add-provider`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ envEntityRef, providerRef: selectedAddRef, organization, project, repoName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? res.statusText);
      setAddOpen(false);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e: any) {
      setAddError(e.message ?? 'Unknown error');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDetach = async () => {
    if (!detachRow || !organization || !project || !repoName) return;
    setDetachLoading(true);
    setDetachError('');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/environments/remove-provider`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ envEntityRef, providerRef: detachRow.ref, organization, project, repoName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? res.statusText);
      setDetachRow(null);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e: any) {
      setDetachError(e.message ?? 'Unknown error');
    } finally {
      setDetachLoading(false);
    }
  };

  return (
    <>
      <InfoCard title="Environment Providers" noPadding>
        {loading ? (
          <Box p={2}><Progress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.headCell}>#</TableCell>
                  <TableCell className={classes.headCell}>Name</TableCell>
                  <TableCell className={classes.headCell}>Prefix</TableCell>
                  <TableCell className={classes.headCell}>Type</TableCell>
                  <TableCell className={classes.headCell}>Subscription</TableCell>
                  <TableCell className={classes.headCell}>Resource Group</TableCell>
                  <TableCell className={classes.headCell}>Region</TableCell>
                  {canModify && <TableCell className={classes.headCell} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {providers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canModify ? 8 : 7}>
                      <Box className={classes.emptyBox}>
                        <Typography variant="body2" color="textSecondary">
                          No providers linked to this environment yet.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  providers.map((p, idx) => (
                    <TableRow key={p.ref} hover>
                      <TableCell className={classes.idCell}>{idx}</TableCell>
                      <TableCell className={classes.nameCell}>{p.name}</TableCell>
                      <TableCell className={classes.dataCell}>{p.prefix}</TableCell>
                      <TableCell className={classes.dataCell}>{p.envType}</TableCell>
                      <TableCell className={classes.dataCell}>{p.azureSubscription}</TableCell>
                      <TableCell className={classes.dataCell}>{p.resourceGroup}</TableCell>
                      <TableCell className={classes.dataCell}>{p.azureRegion}</TableCell>
                      {canModify && (
                        <TableCell align="right">
                          <Tooltip title="Detach provider from this environment">
                            <IconButton
                              size="small"
                              onClick={() => { setDetachError(''); setDetachRow(p); }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {canModify && (
              <Box className={classes.addRow}>
                <Button
                  className={classes.addButton}
                  startIcon={<AddIcon />}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={openAddDialog}
                >
                  Add Provider
                </Button>
              </Box>
            )}

            {!canModify && (
              <Box p={1.5}>
                <Typography variant="caption" color="textSecondary">
                  Re-scaffold this environment to enable provider management.
                </Typography>
              </Box>
            )}
          </>
        )}
      </InfoCard>

      {/* Add Provider Dialog */}
      <Dialog open={addOpen} onClose={() => !addLoading && setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Provider to Environment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select a provider to attach to this environment.
          </DialogContentText>
          <FormControl variant="outlined" className={classes.formControl} fullWidth>
            <InputLabel>Provider</InputLabel>
            <Select
              value={selectedAddRef}
              onChange={e => setSelectedAddRef(e.target.value as string)}
              label="Provider"
              disabled={addLoading || availableProviders.length === 0}
            >
              {availableProviders.map(p => (
                <MenuItem key={p.ref} value={p.ref}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {availableProviders.length === 0 && !addError && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              No unlinked providers available.
            </Typography>
          )}
          {addError && <Typography className={classes.errorText}>{addError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addLoading}>Cancel</Button>
          <Button
            onClick={handleAdd}
            color="primary"
            variant="contained"
            disabled={!selectedAddRef || addLoading}
          >
            {addLoading ? 'Adding…' : 'Add Provider'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detach Confirmation Dialog */}
      <Dialog open={!!detachRow} onClose={() => !detachLoading && setDetachRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Detach Provider</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove <strong>{detachRow?.name}</strong> from this environment? This only removes the
            catalog link — it does not destroy the provider's infrastructure.
          </DialogContentText>
          {detachError && <Typography className={classes.errorText}>{detachError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetachRow(null)} disabled={detachLoading}>Cancel</Button>
          <Button onClick={handleDetach} color="secondary" variant="contained" disabled={detachLoading}>
            {detachLoading ? 'Detaching…' : 'Detach'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
